/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module media-embed/mediaembedui
 */

import { Plugin } from 'ckeditor5/src/core';
import { createDropdown } from 'ckeditor5/src/ui';
import {iconsMap} from '../blocks/blocks-icons-map';

import CustomMovieSrcFormView from "./custom-movie-src-form-view";

/**
 * The media embed UI plugin.
 *
 * @extends module:core/plugin~Plugin
 */
export default class CustomMovieSrcUi extends Plugin {
    /**
     * @inheritDoc
     */
    static get requires() {
        return [  ];
    }

    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'CustomMovieSrcUi';
    }

    /**
     * @inheritDoc
     */
    init() {
        const editor = this.editor;
        const command = editor.commands.get( 'customMovieSrc' );

        editor.ui.componentFactory.add( 'customMovieSrc', locale => {
            const dropdown = createDropdown( locale );
            const mediaForm = new CustomMovieSrcFormView( getFormValidators( editor.t ), editor.locale );

            this._setUpDropdown( dropdown, mediaForm, command, editor );
            this._setUpForm( dropdown, mediaForm, command );

            return dropdown;
        } );
    }

    _setUpDropdown( dropdown, form, command ) {
        const editor = this.editor;
        const t = editor.t;
        const button = dropdown.buttonView;

        dropdown.bind( 'isEnabled' ).to( command );
        dropdown.panelView.children.add( form );

        button.set( {
            label: t( 'Change src' ),
            icon: iconsMap.get('media'),
            tooltip: true,
            withText: false
        } );

        // Note: Use the low priority to make sure the following listener starts working after the
        // default action of the drop-down is executed (i.e. the panel showed up). Otherwise, the
        // invisible form/input cannot be focused/selected.
        button.on( 'open', () => {
            form.disableCssTransitions();

            // Make sure that each time the panel shows up, the URL field remains in sync with the value of
            // the command. If the user typed in the input, then canceled (`urlInputView#fieldView#value` stays
            // unaltered) and re-opened it without changing the value of the media command (e.g. because they
            // didn't change the selection), they would see the old value instead of the actual value of the
            // command.
            form.url = command.value || '';
            form.urlInputView.fieldView.select();
            form.enableCssTransitions();
        }, { priority: 'low' } );

        dropdown.on( 'submit', () => {
            if ( form.isValid() ) {
                editor.execute( 'customMovieSrc', form.url );
                editor.editing.view.focus();
            }
        } );

        dropdown.on( 'change:isOpen', () => form.resetFormStatus() );
        dropdown.on( 'cancel', () => {
            editor.editing.view.focus();
        } );
    }


    _setUpForm( dropdown, form, command ) {
        form.delegate( 'submit', 'cancel' ).to( dropdown );
        form.urlInputView.bind( 'value' ).to( command, 'value' );

        // Form elements should be read-only when corresponding commands are disabled.
        form.urlInputView.bind( 'isReadOnly' ).to( command, 'isEnabled', value => !value );
    }
}

function getFormValidators( t, registry ) {
    return [
        form => {
            if ( !form.url.length ) {
                return t( 'The must not be empty.' );
            }
        },
    ];
}
